import { AxiosResponse } from "axios"
import { action, computed, makeObservable, observable, runInAction } from "mobx"

import { APICode } from "@/api/APICode"
import integrationApi from "@/api/integration"
import {
  EventRule,
  EventRuleAvailability,
  EventRuleAvailabilityPayload,
  EventRuleDimensionOptions,
  EventRuleDimensions,
  EventRuleTopic,
} from "@/api/integration/type"
import i18next from "@/i18n/config"
import stores from "@/stores"
import toast from "@/utils/toast"

import {
  buildStalledStatusTree,
  daysToThresholds,
  mapStalledRule,
  mapTransitDelayRule,
  StalledStatusTreeNode,
} from "./mappers"

/** 单个 topic（运输延迟 / 运输停滞）的数据与请求状态 */
interface TopicState {
  rules: EventRule[]
  options: EventRuleDimensionOptions
  rulesLoading: boolean
  optionsLoading: boolean
  saving: boolean
  rulesLoaded: boolean
  optionsLoaded: boolean
}

/** 业务失败的响应体：HTTP 200，但 body 里带非 200 的 code（如 42202 规则冲突） */
interface BizErrorBody {
  code?: number
  msg?: string
  data?: unknown
}

/**
 * 业务码失败。msg 已由 baseRequest 响应拦截器统一 toast，
 * 抛出它只是为了中断「成功」流程（不再弹成功 toast、调用方保持弹窗打开）。
 */
export class EventRuleBizError extends Error {
  readonly code: number
  /** 失败响应体里的 data，如规则冲突时的 { conflict_rule_pk } */
  readonly payload?: unknown

  constructor(code: number, msg?: string, payload?: unknown) {
    super(msg || `Request failed with code ${code}`)
    this.name = "EventRuleBizError"
    this.code = code
    this.payload = payload
  }
}

/**
 * 校验业务码并取出 payload。
 * 判定条件与 baseRequest 响应拦截器保持一致（code 存在且不等于 200），
 * 这样「拦截器弹了错误提示」和「这里抛错」永远同时发生，不会出现一成一败。
 */
function unwrap<T>(response: AxiosResponse<T>): T | undefined {
  const body = response?.data as (T & BizErrorBody) | undefined

  if (body && typeof body === "object" && body.code !== undefined && body.code !== APICode.OK) {
    throw new EventRuleBizError(body.code, body.msg, body.data)
  }

  return body
}

/**
 * 可用性请求体 -> 缓存 key。
 * 各维度取值排序后参与，勾选顺序不同不会被当成两次不同的请求。
 */
function availabilityCacheKey(payload: EventRuleAvailabilityPayload) {
  const { topic, exclude_rule_id: excludeRuleId, selection } = payload.event_rule
  const normalized = Object.keys(selection)
    .sort()
    .map((dimension) => [dimension, [...(selection[dimension]?.values ?? [])].sort()])

  // exclude_rule_id 必须进 key：同一份 selection 在「新建」「编辑规则 A」「编辑规则 B」
  // 三种场景下服务端算出的占用是不同的，漏掉它会串用缓存
  return JSON.stringify([topic, excludeRuleId ?? null, normalized])
}

/** 全部 topic：规则列表接口一次返回所有 topic，回填时需要遍历 */
const TOPICS: EventRuleTopic[] = ["shipment_transit_delay", "shipment_stalled"]

const createTopicState = (): TopicState => ({
  rules: [],
  options: {},
  rulesLoading: false,
  optionsLoading: false,
  saving: false,
  rulesLoaded: false,
  optionsLoaded: false,
})

/**
 * 事件规则 store：集中管理运输延迟 / 运输停滞两类规则的数据与请求。
 *
 * 对接的接口：
 * - getEventRules            规则列表
 * - getEventRuleDimensionOptions  维度可选项（承运商 / 状态 …）
 * - saveEventRule            新建 / 更新 / 启用停用（复用同一 POST，见 upsert 语义）
 * - checkEventRuleAvailability    目的地可用性检测
 *
 * 组件只消费 store 暴露的视图模型 / 选项 getter 与封装好的动作函数，
 * 不再直接持有 mock 数据。
 */
export default class EventRulesStore {
  @observable transitDelay: TopicState = createTopicState()
  @observable stalled: TopicState = createTopicState()

  /**
   * 可用性检测结果缓存：key 为规范化后的请求体。
   * 同一份 selection 的结果不会变，弹窗里按维度上下文要发多次检测（详见弹窗注释），
   * 缓存后同一步骤里真正打到网络的只有新出现的那个上下文。
   * 规则被增删改后占用情况变化，写入方法里统一清空。
   */
  /** 规则列表的在途请求：接口一次返回所有 topic，两张表并发调用时只发一次 */
  private rulesRequest: Promise<void> | null = null
  private availabilityCache = new Map<string, EventRuleAvailability | undefined>()
  /** 同 key 的在途请求，避免并发的重复请求 */
  private availabilityInflight = new Map<string, Promise<EventRuleAvailability | undefined>>()

  constructor() {
    makeObservable(this)
  }

  /** 规则写入后占用情况变化，之前缓存的可用性结果全部作废 */
  private invalidateAvailabilityCache() {
    this.availabilityCache.clear()
    this.availabilityInflight.clear()
  }

  private stateOf(topic: EventRuleTopic): TopicState {
    return topic === "shipment_transit_delay" ? this.transitDelay : this.stalled
  }

  // ---------------------------------------------------------------------------
  // 视图模型（组件消费）
  // ---------------------------------------------------------------------------

  @computed get transitDelayRuleViewModels() {
    return this.transitDelay.rules.map((rule) =>
      mapTransitDelayRule(rule, this.transitDelay.options, stores.commonStore.countries),
    )
  }

  @computed get stalledRuleViewModels() {
    return this.stalled.rules.map((rule) =>
      mapStalledRule(rule, this.stalled.options),
    )
  }

  /** 运输停滞状态树（由维度选项构建，供选择器与表格展示复用） */
  @computed get stalledStatusTree(): StalledStatusTreeNode[] {
    return buildStalledStatusTree(this.stalled.options.statuses)
  }

  /** 承运商选项（两张表通用） */
  carrierOptions(topic: EventRuleTopic) {
    return this.stateOf(topic).options.carriers ?? []
  }

  /** 发货地点选项（运输延迟用） */
  locationOptions(topic: EventRuleTopic) {
    return this.stateOf(topic).options.locations ?? []
  }

  // ---------------------------------------------------------------------------
  // 读取
  // ---------------------------------------------------------------------------

  /**
   * 加载规则列表。
   *
   * 接口一次返回所有 topic，所以两张表同时挂载时只打一个请求：后到的调用复用在途 promise，
   * 拿到响应后一并回填两个 topic。仍然传 topic 参数（后端保留了该入参）。
   */
  @action.bound
  async loadRules(topic: EventRuleTopic, force = false) {
    const state = this.stateOf(topic)

    if (state.rulesLoaded && !force) {
      return
    }

    if (this.rulesRequest) {
      return this.rulesRequest
    }

    runInAction(() => {
      TOPICS.forEach((t) => {
        this.stateOf(t).rulesLoading = true
      })
    })

    this.rulesRequest = (async () => {
      try {
        const { data } = await integrationApi.getEventRules()
        const rulesByTopic = data?.event_rules ?? {}

        runInAction(() => {
          TOPICS.forEach((t) => {
            const rules = rulesByTopic[t]

            // 只回填响应里真的带了的 topic；请求的那个 topic 即使缺键也按空列表处理，
            // 免得后端哪天改回「只返回请求的 topic」时把另一张表清空
            if (!rules && t !== topic) {
              return
            }

            const topicState = this.stateOf(t)

            topicState.rules = rules ?? []
            topicState.rulesLoaded = true
          })
        })
      } finally {
        runInAction(() => {
          TOPICS.forEach((t) => {
            this.stateOf(t).rulesLoading = false
          })
        })
        this.rulesRequest = null
      }
    })()

    return this.rulesRequest
  }

  @action.bound
  async loadDimensionOptions(topic: EventRuleTopic, force = false) {
    const state = this.stateOf(topic)

    if (state.optionsLoading || (state.optionsLoaded && !force)) {
      return
    }

    state.optionsLoading = true

    try {
      const { data } = await integrationApi.getEventRuleDimensionOptions(topic)

      runInAction(() => {
        state.options = data?.dimension_options ?? {}
        state.optionsLoaded = true
      })
    } finally {
      runInAction(() => {
        state.optionsLoading = false
      })
    }
  }

  // ---------------------------------------------------------------------------
  // 写入
  // ---------------------------------------------------------------------------

  /** 新建规则：POST /event-rules，成功后插入列表头部 */
  @action.bound
  async createRule(topic: EventRuleTopic, dimensions: EventRuleDimensions, days: string[]) {
    const state = this.stateOf(topic)

    state.saving = true

    try {
      const data = unwrap(await integrationApi.saveEventRule({
        event_rule: { topic, dimensions, thresholds: daysToThresholds(days) },
      }))
      const saved = data?.event_rule

      runInAction(() => {
        if (saved) {
          state.rules = [saved, ...state.rules]
        }
      })

      this.invalidateAvailabilityCache()
      toast(i18next.t("common:SavedSuccessfully"))

      return saved
    } finally {
      runInAction(() => {
        state.saving = false
      })
    }
  }

  /** 更新规则（维度 / 天数）：PUT /event-rules/{ruleId}，成功后就地替换 */
  @action.bound
  async updateRule(
    topic: EventRuleTopic,
    ruleId: string,
    patch: { dimensions?: EventRuleDimensions; thresholds?: number[] },
  ) {
    const state = this.stateOf(topic)
    const rule = state.rules.find((r) => r.rule_id === ruleId)

    if (!rule) {
      return undefined
    }

    state.saving = true

    try {
      const data = unwrap(await integrationApi.updateEventRule(ruleId, {
        event_rule: {
          topic,
          dimensions: patch.dimensions ?? rule.dimensions,
          thresholds: patch.thresholds ?? rule.thresholds,
        },
      }))
      const saved = data?.event_rule

      runInAction(() => {
        const index = state.rules.findIndex((r) => r.rule_id === ruleId)

        if (index >= 0 && saved) {
          state.rules[index] = saved
        }
      })

      this.invalidateAvailabilityCache()
      toast(i18next.t("common:SavedSuccessfully"))

      return saved
    } finally {
      runInAction(() => {
        state.saving = false
      })
    }
  }

  /** 修改规则天数：复用既有规则的维度，只改 thresholds */
  @action.bound
  updateRuleDays(topic: EventRuleTopic, ruleId: string, days: string[]) {
    return this.updateRule(topic, ruleId, { thresholds: daysToThresholds(days) })
  }

  /** 启用 / 停用规则：PATCH /event-rules/{ruleId}/switch，成功后就地更新 enabled */
  @action.bound
  async setRuleEnabled(topic: EventRuleTopic, ruleId: string, enabled: boolean) {
    const state = this.stateOf(topic)

    state.saving = true

    try {
      unwrap(await integrationApi.switchEventRule(ruleId, { event_rule: { enabled } }))

      runInAction(() => {
        const rule = state.rules.find((r) => r.rule_id === ruleId)

        if (rule) {
          rule.enabled = enabled
        }
      })

      this.invalidateAvailabilityCache()
      toast(i18next.t(enabled ? "common:EnabledSuccessfully" : "common:DisabledSuccessfully"))
    } catch (error) {
      // 业务码失败时接口 msg 已由拦截器提示，再弹通用文案会把它顶掉
      if (!(error instanceof EventRuleBizError)) {
        toast(i18next.t("common:UpdateFailed"), 4e3, true)
      }
      // 抛出以便调用方保持弹窗打开（仅在成功后关闭）
      throw error
    } finally {
      runInAction(() => {
        state.saving = false
      })
    }
  }

  /** 删除规则：DELETE /event-rules/{ruleId}，成功后从列表移除 */
  @action.bound
  async deleteRule(topic: EventRuleTopic, ruleId: string) {
    const state = this.stateOf(topic)

    state.saving = true

    try {
      unwrap(await integrationApi.deleteEventRule(ruleId))

      runInAction(() => {
        state.rules = state.rules.filter((r) => r.rule_id !== ruleId)
      })

      this.invalidateAvailabilityCache()
      toast(i18next.t("common:DeleteSuccess"))
    } finally {
      runInAction(() => {
        state.saving = false
      })
    }
  }

  /** 可用性检测（两个规则弹窗共用），同一份请求体走缓存 */
  async checkAvailability(payload: EventRuleAvailabilityPayload) {
    const key = availabilityCacheKey(payload)

    if (this.availabilityCache.has(key)) {
      return this.availabilityCache.get(key)
    }

    const inflight = this.availabilityInflight.get(key)

    if (inflight) {
      return inflight
    }

    const request = integrationApi.checkEventRuleAvailability(payload)
      .then((response) => {
        const availability = unwrap(response)?.availability

        this.availabilityCache.set(key, availability)
        return availability
      })
      .finally(() => {
        this.availabilityInflight.delete(key)
      })

    this.availabilityInflight.set(key, request)

    return request
  }
}
